import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Bell, Bug, Camera, FileCheck2, ScanEye, Sparkles } from 'lucide-react';
import './auth-theme.css';

// Fixed choreography for the belt animation - most units are 'pass', two
// per loop are 'reject'. Negative delays desync them immediately on mount
// instead of having every bead bunched up at the left edge on first paint.
const BELT_DURATION = 9;
const BEADS = [
  { type: 'pass', delay: 0 },
  { type: 'pass', delay: -1.1 },
  { type: 'reject', delay: -2.3 },
  { type: 'pass', delay: -3.4 },
  { type: 'pass', delay: -4.5 },
  { type: 'reject', delay: -5.7 },
  { type: 'pass', delay: -6.8 },
  { type: 'pass', delay: -7.9 },
];

const FEATURES = [
  {
    icon: ScanEye,
    title: 'Real-Time Detection',
    body: 'Insects, mould, burn damage, discoloration, and foreign objects flagged the instant they cross the camera.',
  },
  {
    icon: Camera,
    title: 'Multi-Camera Coverage',
    body: 'Watch every line and every shift from one command center, with a full camera-and-date archive to browse.',
  },
  {
    icon: Bell,
    title: 'Instant Alerts',
    body: 'Audible and visual alerts fire the moment a defect is confirmed, so nothing slips past an unwatched screen.',
  },
  {
    icon: FileCheck2,
    title: 'Audit-Ready Logs',
    body: 'Every detection is timestamped, evidenced with a captured frame, and ready to export for compliance.',
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const goDashboard = () => navigate('/');

  return (
    <div className="auth-theme landing-page">
      <div className="auth-blob auth-blob--1" />
      <div className="auth-blob auth-blob--2" />

      <header className="landing-topbar">
        <span className="landing-brand">
          <span className="landing-brand__icon"><img src="/logo.svg" alt="" /></span>
          <span className="landing-brand__text">
            <span className="landing-brand__name">FSQA <span>AI Monitor</span></span>
            <span className="landing-brand__sub">AI based Food Safety and Quality Assurance detection system</span>
          </span>
        </span>
        <button type="button" className="landing-topbar__cta" onClick={goDashboard}>
          Live Dashboard
        </button>
      </header>

      <section className="landing-hero landing-hero--single">
        <div className="landing-hero__copy">
          <span className="landing-badge"><Sparkles size={13} /> AI-POWERED FOOD SAFETY</span>
          <h1>Catch every defect<br />before it leaves <em>the line.</em></h1>
          <p className="landing-tagline">
            Real-time computer vision that spots contamination, mould, burn damage, insects, and
            foreign objects the instant they appear on camera — so a bad batch never makes it to
            a customer.
          </p>
          <div className="landing-cta-row">
            <button type="button" className="landing-cta-primary" onClick={goDashboard}>
              Enter Live Demo <ArrowRight size={17} />
            </button>
            <span className="landing-cta-hint">Instant access · no login required</span>
          </div>
        </div>
      </section>

      <section className="scan-section">
        <p className="scan-widget__stats">
          A defect only has to slip through once. <strong>This is built so it doesn&apos;t.</strong>
        </p>

        <div className="scan-outer">
          <span className="scan-line__label" style={{ left: '55%' }}>AI SCAN</span>
          <div className="scan-track">
            <div className="scan-line" style={{ left: '55%' }} />
            <span className="scan-track__label scan-track__label--pass">PASS → PACKING</span>
            <span className="scan-track__label scan-track__label--reject">↓ REJECT SORT</span>
            {BEADS.map((b, i) => (
              <div
                key={i}
                className={`scan-bead scan-bead--${b.type}`}
                style={{ '--delay': `${b.delay}s`, '--duration': `${BELT_DURATION}s` }}
              >
                <span className="scan-bead__tag">{b.type === 'reject' ? 'REJECT' : 'PASS'}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="scan-widget__caption">
          Every unit, scanned once before it leaves the line — not sampled, not skipped.
        </p>
      </section>

      <section className="landing-features">
        <h2>What it actually catches, on your actual line.</h2>
        <div className="feature-grid">
          {FEATURES.map((f) => (
            <article key={f.title} className="feature-card">
              <span className="feature-card__icon"><f.icon size={18} /></span>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-band">
        <h3>Ready to see it catch something?</h3>
        <button type="button" className="landing-cta-primary" onClick={goDashboard}>
          <Bug size={17} /> Enter Live Demo
        </button>
      </section>

      <footer className="landing-footer">
        FSQA AI Monitor — Food Safety, Automated.
      </footer>
    </div>
  );
}
